import * as ko from 'knockout'
import {Context, Router} from '@profiscience/knockout-contrib-router';
import {EmailDto, emarsysApi, myTripParticipantsApi, myTripsApi, ParticipantDto, TripDto} from "../../api/api-wrapper";
import globalState from '../../global-state';
import {isSharedTrip, participantIconColor, participantInitials, participantName} from "./shareUtils";
import {autobind, computed, observableArray} from "knockout-decorators";
import i18nextko from "../../components/util/i18nko";
import {createConfirmModal} from "../../components/elements/modal/modal";
import {postbox} from "../../components/util/postbox";

class ViewModelContext extends Context {
}

class Form {
    /**
     * The email to enter in the form and add to the recipients.
     */
    public email: KnockoutObservable<string> =
        ko.observable('').extend({required: false, email: true});

    /**
     * Subscription to trim the email field.
     */
    public emailTrimmedSubscription: KnockoutSubscription = this.email.subscribe(newValue => {
        if(newValue) {
            this.email(newValue.trim())
        }
    })

    /**
     * The AGB checkbox.
     */
    public emailAgb: KnockoutObservable<boolean> = ko.observable(false);

    /**
     * The list of recipients.
     */
    public recipients: KnockoutObservableArray<string> = ko.observableArray([]);

    /**
     * The validation status.
     */
    public validationStatus: KnockoutObservable<any> = ko.validatedObservable({
        email: this.email
    });

    /**
     * Check if the form is valid.
     */
    @computed
    public get formValid(): boolean {
        return ( this.recipients().length > 0 || this.emailValid ) && this.emailAgb() === true;
    }

    /**
     * Check if the input for new email is empty.
     */
    @computed
    public get emailEmpty() {
        return this.email().trim().length <= 0;
    }

    /**
     * Check if the input for new email is empty.
     */
    @computed
    public get emailValid() {
        return !this.emailEmpty && this.validationStatus.isValid();
    }

    /**
     * Reset the email input.
     */
    @autobind
    public resetEmail() {
        this.email('');
        this.emailAgb(false);
    }

    /**
     * Add the valid email to the recipient list.
     */
    @autobind
    public addRecipient() {
        if(this.emailValid) {
            this.recipients.push(this.email());
            this.resetEmail();
        }
    }

    /**
     * Remove a recipient from the list.
     * @param email
     */
    @autobind
    public removeRecipient(email: string) {
        this.recipients.splice(this.recipients().indexOf(email), 1);
    }
}

class ViewModel {

    /**
     * The current trip.
     */
    public trip: TripDto;

    /**
     * The email form.
     */
    public form: Form;

    /**
     * Flag if the share URL is visible.
     * It is visible if an error occurrs when the URL is copied to the clipboard.
     */
    public isShareUrlVisible: KnockoutObservable<boolean>;

    constructor(ctx: ViewModelContext) {
        this.trip = globalState.currentTrip;
        this.form = new Form();

        // Initially false to display the URL only if an error while copying to clipboard occurs.
        this.isShareUrlVisible = ko.observable(false);
    }

    /**
     * The trip's participants.
     */
    @computed({pure: true})
    public get participants(): ParticipantDto[] {
        return globalState.currentParticipants;
    }

    public participatePath(): string {
        const shareId = this.trip.shareId ? this.trip.shareId : null;
        const baseUrl = location.protocol + '//' + location.hostname
            + (location.port ? ':' + location.port : '');

        return `${baseUrl}/${config.publicPath}/teilen/${shareId}/teilnehmen`;
    }

    public participantName(participant: ParticipantDto) {
        return participantName(participant);
    }

    public participantInitials(participant: ParticipantDto) {
        return participantInitials(participant);
    }

    public participantIconColor(idx: number) {
        return participantIconColor(idx);
    }

    // noinspection JSUnusedGlobalSymbols
    public isMyParticipation(participant: ParticipantDto): boolean {
        return participant.current === true;
    }

    /**
     * A participant deletes another participant.
     * @param participant
     */
    @autobind
    public deleteParticipantConfirmation(participant: ParticipantDto) {
        const title = i18nextko.t("share.edit.deleteParticipant.confirmTitle");
        const text = i18nextko.t("share.edit.deleteParticipant.confirmText");
        const confirm = i18nextko.t("share.edit.deleteParticipant.confirmBtn");
        const cancel = i18nextko.t("global.cancel");
        const iconCssClass = "fo-icon fo-icon-trash";

        return createConfirmModal(text, title, confirm, cancel, iconCssClass)
            .then(() => {
                globalState.loading(true);
                return myTripParticipantsApi.deleteTripParticipant(String(this.trip.id), participant.id)
                    .then(() => myTripParticipantsApi.getTripParticipants(String(this.trip.id)))
                    .then(response => globalState.currentParticipants = response.data ? response.data : [])
                    .then(() => {
                        globalState.loading(false);
                    })
                    .catch(() => {
                        postbox.addError('share.edit.errors.deleteParticipant');
                        globalState.loading(false);
                    });
            })
            .catch(() => {
                return false;
            });
    }

    /**
     * Logged in participant leaves the trip.
     * @param participant
     */
    @autobind
    public leaveConfirmation(participant: ParticipantDto) {
        const title = i18nextko.t("share.edit.leave.confirmTitle");
        const text = i18nextko.t("share.edit.leave.confirmText");
        const confirm = i18nextko.t("share.edit.leave.confirmBtn");
        const cancel = i18nextko.t("global.cancel");
        const iconCssClass = "fa fa-sign-out";

        return createConfirmModal(text, title, confirm, cancel, iconCssClass)
            .then(() => {
                globalState.loading(true);
                return myTripsApi.deleteTrip(String(this.trip.id))
                    .then(() => {
                        // remove the trip from global state and forward to home view.
                        globalState.trips().splice(globalState.trips().indexOf(this.trip, 0), 1);
                        globalState.loading(false);
                        return Router.update(`/`, {
                            push: true,
                            force: false
                        })
                    })
                    .catch(() => {
                        postbox.addError('share.edit.errors.leave');
                        globalState.loading(false);
                    });
            })
            .catch(() => {
                return false;
            });
    }

    @autobind
    public showCopySuccess() {
        postbox.addSuccess('share.edit.copy.success');
    }

    @autobind
    public showCopyError() {
        this.isShareUrlVisible(true);
        postbox.addError('share.edit.copy.error');
    }

    @autobind
    public sendEmail() {
        // if there is an email in the input which has not been added to the recipients -> add it.
        if(this.form.emailValid) {
            this.form.addRecipient();
        }

        if(this.form.formValid === true) {
            return emarsysApi
                .sendSharedTripInvitation(String(globalState.currentTrip.id), <EmailDto>{
                    recipients: this.form.recipients(),
                    name: `${globalState.user().firstname} ${globalState.user().lastname}`
                })
                .then(() => {
                    globalState.loading(false);
                    postbox.clearErrors();
                    postbox.addSuccess(this.form.recipients().length > 1 ?
                        "share.invite.success.sendEmails" : "share.invite.success.sendEmail");

                    this.form.recipients([]);
                    this.form.emailAgb(false);
                })
                .catch(() => {
                    globalState.loading(false);
                    postbox.addError(this.form.recipients().length > 1 ?
                        "share.invite.errors.sendEmails" : "share.invite.errors.sendEmail");

                    return false;
                });
        }
    }

    /**
     * Dispose subscriptions.
     */
    public dispose() {
        this.form.emailTrimmedSubscription.dispose();
    }
}

export default <KnockoutLazyPageDefinition>{
    viewModel: ViewModel,
    template: require('./edit.html'),
    componentName: "editSharing",
    loader: (ctx: ViewModelContext) => {

        // DRC-658 -> ensure trip is a shared trip with all categories shared
        const tripPromise= isSharedTrip(globalState.currentTrip) ?
            Promise.resolve(globalState.currentTrip) :
            myTripsApi.updateTrip(String(globalState.currentTrip.id), globalState.currentTrip, 'all')
                .then(dataWrapperTrip => {
                    globalState.currentTrip = dataWrapperTrip.data;
                    const idx = globalState.trips().findIndex(trip => trip.id == globalState.currentTrip.id);
                    globalState.trips()[idx] = globalState.currentTrip;
                    return globalState.currentTrip;
                }).catch(() => {
                    postbox.addError('share.create.errors.share');
                });

        // Load participants for the shared trip
        return tripPromise.then(trip =>
            myTripParticipantsApi.getTripParticipants(String(globalState.currentTrip.id))
                .then(response => {
                    globalState.currentParticipants = response.data ? response.data : [];
                    return Promise.resolve();
                })
        );
    }
};
